import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Button, Grid, Modal, Typography } from '@material-ui/core';
import moment from 'moment';

import { fetchSubscriptions } from './queries';
import { useStyles } from './styles';

const TRIAL_DAYS = 14;

interface SubscriptionContextProps {
  currentUser: object | undefined;
  authToken: string;
  subscribed: boolean;
  isPlanChangedForNextCycle: boolean;
  isSubscriptionAlreadyCanceled: boolean;
  subscriptionLoading: boolean;
  subscriptionError: boolean;
  trialDaysRemaining: number;
  currentSubscription: object | undefined;
}

type SubscriptionProviderProps = RouteComponentProps & {
  children: React.ReactNode;
};

export const SubscriptionContext = createContext<SubscriptionContextProps>({
  currentUser: undefined,
  authToken: '',
  subscribed: false,
  isPlanChangedForNextCycle: false,
  isSubscriptionAlreadyCanceled: false,
  subscriptionLoading: true,
  subscriptionError: false,
  trialDaysRemaining: TRIAL_DAYS,
  currentSubscription: undefined,
});

export const useSubscription = () => useContext(SubscriptionContext);

const SubscriptionProvider = ({
  history,
  children
}: SubscriptionProviderProps) => {
  const [authToken, setAuthToken] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<any>();
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number>(TRIAL_DAYS);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const classes = useStyles();

  const handleBuySubscription = () => {
    setShowSubscriptionModal(false);

    history.push('/home/subscriptions');
  };

  useEffect(() => {
    const loadStorage = () => {
      const token = localStorage.getItem("authToken");
      const user = localStorage.getItem("currentUser");

      if (token) {
        setAuthToken(token);
      }
      if (user) {
        setCurrentUser(JSON.parse(user));
      }
    };

    window.addEventListener('storage', loadStorage);

    return () => {
      window.removeEventListener('storage', loadStorage);
    };
  }, []);

  const {
    data,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => fetchSubscriptions(authToken!),
    enabled: !!authToken,
    staleTime: 86_400_000, // 1 day in ms,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const checkSubscription = () => {
    const currentSubscription = data?.data[0]?.attributes;
    
    let trialPeriodEndTime;
    const currentTime = moment(new Date(new Date().setHours(0, 0, 0)));
    if (currentUser) {
      let createdAt = new Date(
        new Date(currentUser?.data?.attributes?.created_at as Date).setHours(23)
      );
      trialPeriodEndTime = moment(createdAt).add(TRIAL_DAYS, "days");
    }
    
    let trialPeriod = trialPeriodEndTime?.diff(currentTime, "days");
    let currentSubscriptionStatus = currentSubscription?.subscription_status;
    if (trialPeriod && trialPeriod < 1) {
      if (!currentSubscriptionStatus || (currentSubscriptionStatus != "active")) {
        setShowSubscriptionModal(true);
      }
    } else {
      setTrialDaysRemaining(trialPeriod!);
    }
  };

  useEffect(() => {
    if (!data || !currentUser) {
      return;
    }

    checkSubscription();
  }, [data, currentUser])


  return (
    <SubscriptionContext.Provider value={{
      authToken: authToken,
      currentUser: currentUser,
      subscribed: data?.data.length > 0,
      isPlanChangedForNextCycle: data?.data[0]?.attributes.scheduled_subscription_status ===
            "active" &&
          new Date(
            data?.data[0]?.attributes?.scheduled_subscription_date
          ).getTime() > new Date().getTime(),
      isSubscriptionAlreadyCanceled: data?.data[0]?.attributes?.sub_canceled_at &&
          data?.data[0]?.attributes?.sub_cancel_at &&
          new Date(data?.data[0]?.attributes?.sub_cancel_at).getTime() >
            new Date(
              data?.data[0]?.attributes?.sub_canceled_at
            ).getTime(),
      subscriptionLoading: isLoading,
      subscriptionError: isError,
      trialDaysRemaining: trialDaysRemaining, 
      currentSubscription: data ? data?.data[0]?.attributes : undefined 
    }}>
      <Modal open={!isLoading && showSubscriptionModal}>
        <div
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
          className={classes.trialPaper}
        >
          <Grid container>
            <Grid xs={12} item>
              <Typography
                color="error"
                variant="h2"
                id="simple-modal-description"
              >
                Your trial period has been ended.
              </Typography>
              <Typography
                color="textSecondary"
                variant="h4"
                id="simple-modal-description"
              >
                To continue working with{' '}
                <Typography component="span" color="primary">
                  Smartpath.co
                </Typography>{' '}
                it is necessary to purchase a subscription of our product
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Grid container justifyContent="flex-end">
                <Button
                  color="primary"
                  onClick={handleBuySubscription}
                  variant="contained"
                >
                  Buy subscription
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Modal>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default withRouter(SubscriptionProvider);

